import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import {
  X, Cog, Calendar, ClipboardList, Shield, Activity, BookOpen,
  ChevronRight, CheckCircle2, Clock, ShoppingCart, HardHat, ShieldCheck,
  Wrench, FileText, AlertTriangle, PauseCircle
} from "lucide-react";
import {
  CRITICALITY, JOB_TYPES, JOB_STATUS_CONFIG, WO_STATUS_CONFIG,
  PRIORITY_CONFIG, StatusBadge, PriorityBadge,
} from "./PMSConstants";

interface PMSDetailPanelProps {
  type: "equipment" | "job" | "work_order" | null;
  item: any;
  onClose: () => void;
  equipment?: any[];
  spareParts?: any[];
  conditionData?: any[];
  jobs?: any[];
  workOrders?: any[];
  onAction?: (action: string, data: any) => void;
  isChiefEngineer?: boolean;
  isSuperintendent?: boolean;
}

const PMSDetailPanel = ({
  type, item, onClose, equipment = [], spareParts = [], conditionData = [],
  jobs = [], workOrders = [], onAction, isChiefEngineer, isSuperintendent,
}: PMSDetailPanelProps) => {
  if (!type || !item) return null;

  const renderEquipmentDetail = () => {
    const eqJobs = jobs.filter((j: any) => j.equipment_id === item.id);
    const eqWOs = workOrders.filter((w: any) => w.equipment_id === item.id);
    const eqCond = conditionData.filter((c: any) => c.equipment_id === item.id);
    const eqParts = spareParts.filter((p: any) => (p.linked_equipment_ids as any)?.includes?.(item.id));
    const childEquipment = equipment.filter((e: any) => e.parent_equipment_id === item.id);

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="p-4 bg-teal/5 rounded-lg border border-teal/20">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CRITICALITY.find(c => c.value === item.criticality)?.color || ""}`}>{item.criticality}</span>
            <span className="font-mono text-xs text-muted-foreground">{item.equipment_code || "—"}</span>
          </div>
          <h3 className="font-display text-lg">{item.equipment_name}</h3>
          <p className="text-sm text-muted-foreground">{item.vessel_name} · {item.category}</p>
        </div>

        {/* Specs Grid */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "System", value: item.system_name || "—" },
            { label: "Component ID", value: item.component_id_code || item.equipment_code || "—" },
            { label: "Manufacturer", value: item.manufacturer || "—" },
            { label: "Model", value: item.model || "—" },
            { label: "Serial Number", value: item.serial_number || "—" },
            { label: "Location", value: item.location_on_vessel || "—" },
            { label: "Sub-System", value: item.sub_system || "—" },
            { label: "Install Date", value: item.install_date || "—" },
            { label: "Maintenance Source", value: item.maintenance_source || "—" },
            { label: "Running Hours", value: item.running_hours != null ? `${item.running_hours} hrs` : "—" },
          ].map(s => (
            <div key={s.label}>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
              <p className="text-sm">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Technical Specs */}
        {item.technical_specs && (
          <div className="p-3 bg-muted/30 rounded-lg">
            <p className="text-[10px] text-muted-foreground mb-1">Technical Specs</p>
            <p className="text-xs">{item.technical_specs}</p>
          </div>
        )}

        {/* Class Survey Flag */}
        {item.class_survey_item && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-500/10 rounded border border-blue-200 dark:border-blue-500/20">
            <Shield className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-blue-700 dark:text-blue-400 font-medium">ABS Class Survey Item</span>
          </div>
        )}

        {/* Child Equipment */}
        {childEquipment.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-display mb-2 flex items-center gap-1"><Cog className="w-3 h-3 text-teal" />Sub-Components ({childEquipment.length})</p>
              <div className="space-y-1">
                {childEquipment.map((child: any) => (
                  <button key={child.id} className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 text-left transition-colors"
                    onClick={() => onAction?.("selectEquipment", child)}>
                    <Cog className="w-3 h-3 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs truncate">{child.equipment_name}</p>
                      <p className="text-[10px] text-muted-foreground">{child.equipment_code || "—"}</p>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${CRITICALITY.find(c => c.value === child.criticality)?.color || ""}`}>{child.criticality}</span>
                    <ChevronRight className="w-3 h-3 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Linked Jobs */}
        {eqJobs.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-display mb-2 flex items-center gap-1"><ClipboardList className="w-3 h-3 text-teal" />Maintenance Jobs ({eqJobs.length})</p>
              <div className="space-y-1">
                {eqJobs.map((job: any) => (
                  <button key={job.id} className="w-full flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 text-left transition-colors"
                    onClick={() => onAction?.("selectJob", job)}>
                    <Wrench className={`w-3 h-3 ${job.priority === "critical" ? "text-red-500" : "text-muted-foreground"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs truncate">{job.title}</p>
                      <p className="text-[10px] text-muted-foreground">{job.job_number} · {job.next_due_date ? format(new Date(job.next_due_date), "dd MMM yy") : "No date"}</p>
                    </div>
                    <StatusBadge status={job.status} config={JOB_STATUS_CONFIG} />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Condition Readings */}
        {eqCond.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-display mb-2 flex items-center gap-1"><Activity className="w-3 h-3 text-teal" />Latest Readings</p>
              <div className="grid grid-cols-2 gap-2">
                {eqCond.slice(0, 6).map((c: any) => (
                  <div key={c.id} className={`text-xs p-2 rounded ${c.status === "critical" ? "bg-red-50 dark:bg-red-500/10 text-red-700" : c.status === "warning" ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700" : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700"}`}>
                    <span className="font-display">{c.parameter_label}:</span> {c.reading_value} {c.unit}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-2 flex-wrap pt-2">
          <Button size="sm" className="bg-teal hover:bg-teal-light text-white text-xs" onClick={() => onAction?.("createJob", item)}>
            <ClipboardList className="w-3 h-3 mr-1" />Create Job
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => onAction?.("logReading", item)}>
            <Activity className="w-3 h-3 mr-1" />Log Reading
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => onAction?.("logHours", item)}>
            <Clock className="w-3 h-3 mr-1" />Log Hours
          </Button>
        </div>
      </div>
    );
  };

  const renderJobDetail = () => {
    const eq = equipment.find((e: any) => e.id === item.equipment_id);
    const jobCond = conditionData.filter((c: any) => c.equipment_id === item.equipment_id);

    return (
      <div className="space-y-4">
        <div className="p-4 bg-teal/5 rounded-lg border border-teal/20">
          <div className="flex items-center gap-2 mb-1">
            <PriorityBadge priority={item.priority} />
            <StatusBadge status={item.status} config={JOB_STATUS_CONFIG} />
            {item.class_requirement && <Badge variant="outline" className="text-[8px]">CLASS</Badge>}
          </div>
          <h3 className="font-display text-lg mt-1">{item.title}</h3>
          <p className="text-sm text-muted-foreground">{item.job_number} · {item.vessel_name}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Type", value: JOB_TYPES.find(t => t.value === item.job_type)?.label || item.job_type },
            { label: "Department", value: item.department },
            { label: "Due Date", value: item.next_due_date ? format(new Date(item.next_due_date), "dd MMM yyyy") : "—" },
            { label: "Est. Duration", value: item.estimated_duration_hours ? `${item.estimated_duration_hours}h` : "—" },
            { label: "Interval", value: item.interval_days ? `${item.interval_days} days` : item.interval_hours ? `${item.interval_hours} hrs` : "—" },
            { label: "Last Completed", value: item.last_completed_at ? format(new Date(item.last_completed_at), "dd MMM yyyy") : "Never" },
          ].map(s => (
            <div key={s.label}>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
              <p className="text-sm">{s.value}</p>
            </div>
          ))}
        </div>

        {eq && (
          <>
            <Separator />
            <button className="w-full flex items-center gap-2 p-3 rounded-md hover:bg-muted/50 text-left transition-colors"
              onClick={() => onAction?.("selectEquipment", eq)}>
              <Cog className="w-4 h-4 text-teal" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-display">{eq.equipment_name}</p>
                <p className="text-[10px] text-muted-foreground">{eq.equipment_code} · {eq.manufacturer || ""}</p>
              </div>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
            </button>
          </>
        )}

        {(item.regulatory_reference || item.class_requirement || item.requires_alongside) && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-display mb-2 flex items-center gap-1 text-red-600"><Shield className="w-3 h-3" />Regulatory</p>
              <div className="flex flex-wrap gap-2">
                {item.class_requirement && <Badge variant="outline" className="text-[10px] border-red-300">Class Requirement</Badge>}
                {item.requires_alongside && <Badge variant="outline" className="text-[10px] border-amber-300">Requires Alongside</Badge>}
                {item.regulatory_reference && <Badge variant="outline" className="text-[10px]">{item.regulatory_reference}</Badge>}
              </div>
            </div>
          </>
        )}

        {item.procedures && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-display mb-2 flex items-center gap-1"><BookOpen className="w-3 h-3 text-teal" />Procedures</p>
              <pre className="text-xs whitespace-pre-wrap font-body bg-muted/30 p-3 rounded max-h-40 overflow-y-auto">{item.procedures}</pre>
            </div>
          </>
        )}

        {jobCond.length > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-display mb-2 flex items-center gap-1"><Activity className="w-3 h-3" />Condition Data</p>
              <div className="grid grid-cols-2 gap-2">
                {jobCond.slice(0, 4).map((c: any) => (
                  <div key={c.id} className={`text-xs p-2 rounded ${c.status === "critical" ? "bg-red-50 dark:bg-red-500/10 text-red-700" : c.status === "warning" ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700" : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700"}`}>
                    {c.parameter_label}: {c.reading_value} {c.unit}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="flex gap-2 flex-wrap pt-2">
          {item.status === "scheduled" && (
            <Button size="sm" className="bg-teal hover:bg-teal-light text-white text-xs" onClick={() => onAction?.("startJob", item)}>Start</Button>
          )}
          {item.status === "in_progress" && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => onAction?.("completeJob", item)}>Complete</Button>
          )}
          <Button size="sm" variant="outline" className="text-xs" onClick={() => onAction?.("createWO", item)}>
            <FileText className="w-3 h-3 mr-1" />Create WO
          </Button>
          {isChiefEngineer && (item.status === "scheduled" || item.status === "overdue") && (
            <Button size="sm" variant="outline" className="text-xs text-purple-600" onClick={() => onAction?.("deferJob", item)}>
              <PauseCircle className="w-3 h-3 mr-1" />Defer
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderWODetail = () => (
    <div className="space-y-4">
      <div className="p-4 bg-teal/5 rounded-lg border border-teal/20">
        <div className="flex items-center gap-2 mb-1">
          <PriorityBadge priority={item.priority} />
          <StatusBadge status={item.status} config={WO_STATUS_CONFIG} />
          <Badge variant="outline" className="text-[10px]">{item.work_type}</Badge>
        </div>
        <h3 className="font-display text-lg mt-1">{item.title}</h3>
        <p className="text-sm text-muted-foreground font-mono">{item.work_order_number} · {item.vessel_name}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "Equipment", value: item.equipment_name || "—" },
          { label: "Department", value: item.department },
          { label: "Assigned To", value: item.assigned_to_name || "Unassigned" },
          { label: "Est. Hours", value: item.estimated_hours || "—" },
        ].map(s => (
          <div key={s.label}>
            <p className="text-[10px] text-muted-foreground">{s.label}</p>
            <p className="text-sm">{s.value}</p>
          </div>
        ))}
      </div>

      {item.description && (
        <div>
          <p className="text-[10px] text-muted-foreground">Description</p>
          <p className="text-sm mt-1">{item.description}</p>
        </div>
      )}

      {/* JSA */}
      {item.jsa_required && (
        <>
          <Separator />
          <div className="p-3 rounded-lg bg-amber-50/50 dark:bg-amber-500/5 border border-amber-200">
            <p className="text-xs font-display flex items-center gap-1 text-amber-700 mb-2"><HardHat className="w-4 h-4" />Job Safety Analysis</p>
            {item.jsa_hazards && <div className="mb-2"><p className="text-[10px] text-muted-foreground">Hazards</p><p className="text-xs">{item.jsa_hazards}</p></div>}
            {item.jsa_controls && <div className="mb-2"><p className="text-[10px] text-muted-foreground">Controls</p><p className="text-xs">{item.jsa_controls}</p></div>}
            <div className="flex items-center gap-2">
              {item.jsa_approved_by ? (
                <><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-xs">Approved by {item.jsa_approved_by_name}</span></>
              ) : isChiefEngineer ? (
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white text-xs" onClick={() => onAction?.("approveJSA", item)}>
                  <ShieldCheck className="w-3 h-3 mr-1" />Approve JSA
                </Button>
              ) : <span className="text-xs text-amber-600">⚠ Pending JSA Approval</span>}
            </div>
          </div>
        </>
      )}

      {/* Approval Chain */}
      <Separator />
      <div>
        <p className="text-xs font-display mb-2">Approval Chain</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 p-2 rounded bg-muted/30 flex-1">
            {item.chief_engineer_approved ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
            <div>
              <p className="text-[10px] font-display">Chief Engineer</p>
              {item.chief_engineer_name && <p className="text-[9px] text-muted-foreground">{item.chief_engineer_name}</p>}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="flex items-center gap-2 p-2 rounded bg-muted/30 flex-1">
            {item.superintendent_approved ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
            <div>
              <p className="text-[10px] font-display">Superintendent</p>
              {item.superintendent_name && <p className="text-[9px] text-muted-foreground">{item.superintendent_name}</p>}
            </div>
          </div>
        </div>
      </div>

      {item.linked_po_number && (
        <div className="flex items-center gap-2 p-3 bg-teal/5 rounded-lg">
          <ShoppingCart className="w-4 h-4 text-teal" />
          <p className="text-sm">PO: <span className="font-mono font-bold text-teal">{item.linked_po_number}</span></p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap pt-2">
        {item.status === "open" && (
          <Button size="sm" className="bg-teal hover:bg-teal-light text-white text-xs" onClick={() => onAction?.("startWO", item)}>Start Work</Button>
        )}
        {item.status === "in_progress" && (
          <>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => onAction?.("completeWO", item)}>Complete</Button>
            <Button size="sm" variant="outline" className="text-xs text-orange-600" onClick={() => onAction?.("awaitPartsWO", item)}>Awaiting Parts</Button>
          </>
        )}
        {item.status === "completed" && isSuperintendent && (
          <Button size="sm" variant="outline" className="text-xs" onClick={() => onAction?.("closeWO", item)}>Close WO</Button>
        )}
        {!item.chief_engineer_approved && isChiefEngineer && item.status !== "closed" && item.status !== "cancelled" && (
          <Button size="sm" variant="outline" className="text-xs text-emerald-600" onClick={() => onAction?.("approveCE", item)}>CE Approve</Button>
        )}
        {item.chief_engineer_approved && !item.superintendent_approved && isSuperintendent && item.status !== "closed" && item.status !== "cancelled" && (
          <Button size="sm" variant="outline" className="text-xs text-emerald-600" onClick={() => onAction?.("approveSupt", item)}>Supt Approve</Button>
        )}
        {!item.linked_po_number && item.status !== "closed" && item.status !== "cancelled" && (
          <Button size="sm" variant="outline" className="text-xs" onClick={() => onAction?.("createPO", item)}>
            <ShoppingCart className="w-3 h-3 mr-1" />Create PO
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-[400px] shrink-0 border-l border-border bg-card flex flex-col h-full">
      {/* Detail Panel Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2">
          {type === "equipment" && <Cog className="w-4 h-4 text-teal" />}
          {type === "job" && <ClipboardList className="w-4 h-4 text-teal" />}
          {type === "work_order" && <FileText className="w-4 h-4 text-teal" />}
          <span className="text-xs font-display uppercase tracking-wider text-muted-foreground">
            {type === "equipment" ? "Equipment Detail" : type === "job" ? "Job Card" : "Work Order"}
          </span>
        </div>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {type === "equipment" && renderEquipmentDetail()}
          {type === "job" && renderJobDetail()}
          {type === "work_order" && renderWODetail()}
        </div>
      </ScrollArea>
    </div>
  );
};

export default PMSDetailPanel;
